import React from 'react'
import classnames from 'classnames'
import type { TariffHistorySource, TariffStatus } from '../../../apis/types'
import { FieldTooltip, STATUS_GLOSSARY } from './TariffFieldKey'

const STATUS_LABELS: Record<string, string> = {
    WAITING_FOR_APPROVAL: 'Pending',
    APPROVED: 'Active',
    ARCHIVED: 'Inactive',
    PENDING: 'Pending',
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
}

/** Matches TariffStatusSelect tones so list/detail chips stay consistent. */
const STATUS_CHIP_CLASS: Record<string, string> = {
    WAITING_FOR_APPROVAL:
        'bg-amber-50 text-amber-950 ring-amber-400/50 dark:bg-amber-950/40 dark:text-amber-100 dark:ring-amber-500/50',
    PENDING:
        'bg-amber-50 text-amber-950 ring-amber-400/50 dark:bg-amber-950/40 dark:text-amber-100 dark:ring-amber-500/50',
    APPROVED:
        'bg-emerald-50 text-emerald-950 ring-emerald-400/50 dark:bg-emerald-950/40 dark:text-emerald-100 dark:ring-emerald-500/50',
    ACTIVE:
        'bg-emerald-50 text-emerald-950 ring-emerald-400/50 dark:bg-emerald-950/40 dark:text-emerald-100 dark:ring-emerald-500/50',
    ARCHIVED:
        'bg-slate-100 text-slate-800 ring-slate-400/50 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-500/50',
    INACTIVE:
        'bg-slate-100 text-slate-800 ring-slate-400/50 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-500/50',
}

export const tariffStatusLabel = (status: TariffStatus): string => STATUS_LABELS[status] ?? status

export const TariffLatestBadge: React.FC<{ className?: string }> = ({ className }) => (
    <span
        className={classnames(
            'inline-flex shrink-0 items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200',
            className
        )}
    >
        Latest
    </span>
)

/** Display label for utility type column (ESP_DA → DA). */
export const formatUtilityTypeLabel = (utilityType: string | null | undefined): string => {
    if (utilityType == null || utilityType === '') return ''
    if (utilityType === 'ESP_DA') return 'DA'
    return utilityType
}

export const formatTariffSourceLabel = (source: TariffHistorySource | null | undefined): string => {
    if (source === 'GENABILITY') return 'Genability'
    if (source === 'MANUAL') return 'Manual'
    if (source === 'UNKNOWN') return 'Unknown'
    return ''
}

const ENUM_BADGE_BASE =
    'inline-flex max-w-full truncate rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset'

const ENUM_BADGE_SLATE =
    'bg-slate-100 text-slate-700 ring-slate-500/15 dark:bg-slate-700 dark:text-slate-200 dark:ring-slate-400/20'

/** Type column hues stay off PG&E blue, SCE orange, and SDG&E green. */
const TYPE_CHIP_CLASS: Record<'IOU' | 'CCA' | 'ESP_DA', string> = {
    IOU: 'bg-sky-50 text-sky-950 ring-sky-400/50 dark:bg-sky-900 dark:text-sky-100 dark:ring-sky-400/40',
    CCA: 'bg-teal-50 text-teal-950 ring-teal-400/50 dark:bg-teal-900 dark:text-teal-100 dark:ring-teal-400/40',
    ESP_DA:
        'bg-violet-50 text-violet-950 ring-violet-400/50 dark:bg-violet-900 dark:text-violet-100 dark:ring-violet-400/40',
}

/** Source column hues stay off Type and utility chips. */
const SOURCE_CHIP_CLASS: Record<TariffHistorySource, string> = {
    GENABILITY:
        'bg-indigo-50 text-indigo-950 ring-indigo-400/50 dark:bg-indigo-900 dark:text-indigo-100 dark:ring-indigo-400/40',
    MANUAL: 'bg-amber-50 text-amber-950 ring-amber-400/50 dark:bg-amber-900 dark:text-amber-100 dark:ring-amber-400/40',
    UNKNOWN:
        'bg-slate-100 text-slate-800 ring-slate-400/50 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-400/30',
}

/** Shared row hover for tariff manager custom tables. Overlay so tone backgrounds stay visible. */
export const TARIFF_TABLE_ROW_HOVER =
    "relative isolate after:pointer-events-none after:absolute after:inset-0 after:z-0 after:bg-slate-900/[0.04] after:opacity-0 after:content-[''] after:transition-opacity after:duration-150 group-hover:after:opacity-100 dark:after:bg-white/[0.07]"

const EnumBadge: React.FC<{ label: React.ReactNode; colorClass: string; title?: string }> = ({
    label,
    colorClass,
    title,
}) => {
    const badge = <span className={classnames(ENUM_BADGE_BASE, colorClass)}>{label}</span>
    if (title == null) return badge
    return <FieldTooltip content={title}>{badge}</FieldTooltip>
}

export type RateEnumKind = 'type' | 'category' | 'season' | 'tou' | 'group'

const RATE_ENUM_CHIP: Record<string, string> = {
    rose: 'bg-rose-50 text-rose-950 ring-rose-400/50 dark:bg-rose-900 dark:text-rose-100 dark:ring-rose-400/40',
    fuchsia:
        'bg-fuchsia-50 text-fuchsia-950 ring-fuchsia-400/50 dark:bg-fuchsia-900 dark:text-fuchsia-100 dark:ring-fuchsia-400/40',
    purple:
        'bg-purple-50 text-purple-950 ring-purple-400/50 dark:bg-purple-900 dark:text-purple-100 dark:ring-purple-400/40',
    indigo:
        'bg-indigo-50 text-indigo-950 ring-indigo-400/50 dark:bg-indigo-900 dark:text-indigo-100 dark:ring-indigo-400/40',
    violet:
        'bg-violet-50 text-violet-950 ring-violet-400/50 dark:bg-violet-900 dark:text-violet-100 dark:ring-violet-400/40',
    blue: 'bg-blue-50 text-blue-950 ring-blue-400/50 dark:bg-blue-900 dark:text-blue-100 dark:ring-blue-400/40',
    sky: 'bg-sky-50 text-sky-950 ring-sky-400/50 dark:bg-sky-900 dark:text-sky-100 dark:ring-sky-400/40',
    cyan: 'bg-cyan-50 text-cyan-950 ring-cyan-400/50 dark:bg-cyan-900 dark:text-cyan-100 dark:ring-cyan-400/40',
    teal: 'bg-teal-50 text-teal-950 ring-teal-400/50 dark:bg-teal-900 dark:text-teal-100 dark:ring-teal-400/40',
    amber:
        'bg-amber-50 text-amber-950 ring-amber-400/50 dark:bg-amber-900 dark:text-amber-100 dark:ring-amber-400/40',
    orange:
        'bg-orange-50 text-orange-950 ring-orange-400/50 dark:bg-orange-900 dark:text-orange-100 dark:ring-orange-400/40',
    yellow:
        'bg-yellow-50 text-yellow-950 ring-yellow-400/50 dark:bg-yellow-900 dark:text-yellow-100 dark:ring-yellow-400/40',
    lime: 'bg-lime-50 text-lime-950 ring-lime-400/50 dark:bg-lime-900 dark:text-lime-100 dark:ring-lime-400/40',
    emerald:
        'bg-emerald-50 text-emerald-950 ring-emerald-400/50 dark:bg-emerald-900 dark:text-emerald-100 dark:ring-emerald-400/40',
    red: 'bg-red-50 text-red-950 ring-red-400/50 dark:bg-red-900 dark:text-red-100 dark:ring-red-400/40',
    pink: 'bg-pink-50 text-pink-950 ring-pink-400/50 dark:bg-pink-900 dark:text-pink-100 dark:ring-pink-400/40',
    stone: 'bg-stone-100 text-stone-800 ring-stone-400/50 dark:bg-stone-800 dark:text-stone-100 dark:ring-stone-400/40',
}

const RATE_ENUM_BY_KIND: Record<RateEnumKind, Record<string, string>> = {
    type: {
        CHARGE: RATE_ENUM_CHIP.rose,
        ADJUSTMENT: RATE_ENUM_CHIP.fuchsia,
        MAX: RATE_ENUM_CHIP.purple,
    },
    category: {
        TOU: RATE_ENUM_CHIP.indigo,
        DEMAND: RATE_ENUM_CHIP.violet,
        NBC: RATE_ENUM_CHIP.yellow,
        PDP: RATE_ENUM_CHIP.pink,
        NON_TOU_NON_NBC: ENUM_BADGE_SLATE,
    },
    season: {
        SUMMER: RATE_ENUM_CHIP.amber,
        WINTER: RATE_ENUM_CHIP.cyan,
        ALL: ENUM_BADGE_SLATE,
    },
    tou: {
        PEAK: RATE_ENUM_CHIP.red,
        MID_PEAK: RATE_ENUM_CHIP.orange,
        PARTIAL_PEAK: RATE_ENUM_CHIP.amber,
        OFF_PEAK: RATE_ENUM_CHIP.sky,
        SUPER_OFF_PEAK: RATE_ENUM_CHIP.teal,
        ALL: ENUM_BADGE_SLATE,
    },
    group: {
        GENERATION: RATE_ENUM_CHIP.emerald,
        DELIVERY: RATE_ENUM_CHIP.blue,
        NBC: RATE_ENUM_CHIP.yellow,
        DEMAND: RATE_ENUM_CHIP.orange,
        SERVICE: RATE_ENUM_CHIP.stone,
        BUNDLED_FEE: RATE_ENUM_CHIP.lime,
        PDP: RATE_ENUM_CHIP.red,
        NONE: ENUM_BADGE_SLATE,
    },
}

export function rateEnumChipClass(kind: RateEnumKind, label: string): string {
    if (kind === 'category' && label.startsWith('CUSTOMER')) return RATE_ENUM_CHIP.stone
    return RATE_ENUM_BY_KIND[kind][label] ?? ENUM_BADGE_SLATE
}

export const RateEnumBadge: React.FC<{
    label: string | null
    kind: RateEnumKind
    title?: string
    children?: React.ReactNode
}> = ({ label, kind, title, children }) => {
    if (label == null || label === '') return null
    return (
        <EnumBadge
            label={children ?? label}
            colorClass={rateEnumChipClass(kind, label)}
            title={title}
        />
    )
}

export const TariffTypeChip: React.FC<{ type: string | null | undefined; title?: string }> = ({
    type,
    title,
}) => {
    const label = formatUtilityTypeLabel(type)
    if (label === '') return null
    const colorClass =
        type === 'IOU' || type === 'CCA' || type === 'ESP_DA' ? TYPE_CHIP_CLASS[type] : ENUM_BADGE_SLATE
    return <EnumBadge label={label} colorClass={colorClass} title={title} />
}

export const TariffSourceChip: React.FC<{ source: TariffHistorySource | null | undefined }> = ({
    source,
}) => {
    const label = formatTariffSourceLabel(source)
    if (label === '') return null
    const colorClass = source != null ? SOURCE_CHIP_CLASS[source] : ENUM_BADGE_SLATE
    return <EnumBadge label={label} colorClass={colorClass} />
}

export const TariffStatusChip: React.FC<{ status: TariffStatus; className?: string }> = ({
    status,
    className,
}) => (
    <FieldTooltip content={STATUS_GLOSSARY[status] ?? tariffStatusLabel(status)}>
        <span
            className={classnames(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
                STATUS_CHIP_CLASS[status],
                className
            )}
        >
            {tariffStatusLabel(status)}
        </span>
    </FieldTooltip>
)

const UTILITY_CHIP_COLORS: Record<string, string> = {
    'PG&E': 'bg-blue-50 text-blue-800 ring-blue-600/20 dark:bg-blue-400/10 dark:text-blue-300',
    SCE: 'bg-orange-50 text-orange-800 ring-orange-600/20 dark:bg-orange-400/10 dark:text-orange-300',
    'SDG&E': 'bg-green-50 text-green-800 ring-green-600/20 dark:bg-green-400/10 dark:text-green-300',
}

export const TariffUtilityChip: React.FC<{
    shortName: string | null
    utilityName?: string | null
    utilityType?: string | null
    className?: string
}> = ({ shortName, utilityName, utilityType, className }) => {
    if (shortName == null && utilityName == null) return null

    const isNonIou = utilityType === 'CCA' || utilityType === 'ESP_DA'
    const color = isNonIou
        ? 'bg-slate-50 text-slate-700 ring-slate-500/20 dark:bg-slate-400/10 dark:text-slate-300'
        : UTILITY_CHIP_COLORS[shortName ?? ''] ??
          'bg-slate-50 text-slate-700 ring-slate-500/20 dark:bg-slate-400/10 dark:text-slate-300'

    let label: string
    if (isNonIou) {
        if (utilityName != null && utilityName !== '' && shortName != null && shortName !== '') {
            label = `${utilityName} (${shortName})`
        } else {
            label = utilityName || shortName || ''
        }
    } else {
        label = shortName ?? utilityName ?? ''
    }

    if (label === '') return null

    return (
        <span
            className={classnames(
                'inline-flex max-w-full items-center truncate rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
                color,
                className
            )}
            title={label}
        >
            {label}
        </span>
    )
}
